import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/shared/ui/providers/AuthSessionProvider'
import { useUpdateMyInfosMutation, useGetMyInfosQuery } from '@/core/graphql/hooks'
import { languages, SupportedLanguage } from '@/lib/i18n/settings'

/**
 * Hook that synchronizes the patient's language preference with the backend.
 *
 * Features:
 * 1. On login: Redirects to patient's stored language preference
 * 2. On language change: Updates the backend with the new language
 *
 * This ensures:
 * - Emails and notifications are sent in the patient's preferred language
 * - Language preference persists across sessions
 */
export function useLanguageSync() {
  const { i18n } = useTranslation()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const { data, loading: queryLoading } = useGetMyInfosQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first',
  })

  const [updateMyInfos] = useUpdateMyInfosMutation()

  // Track if we've already synced on initial load
  const hasInitialSynced = useRef(false)
  // Track the last synced language to prevent redundant updates
  const lastSyncedLanguage = useRef<string | null>(null)

  const patientLanguage = data?.myInfos?.userInfo?.language as SupportedLanguage | undefined | null

  // Redirect to stored language on initial load (login)
  useEffect(() => {
    if (isAuthenticated && !queryLoading && patientLanguage && !hasInitialSynced.current && languages.includes(patientLanguage)) {
      hasInitialSynced.current = true
      lastSyncedLanguage.current = patientLanguage

      // Get current URL language
      const pathParts = location.pathname.split('/')
      const currentUrlLang = pathParts[1]

      // If stored language differs from URL language, redirect
      if (currentUrlLang !== patientLanguage && languages.includes(currentUrlLang as SupportedLanguage)) {
        const newPath = `/${patientLanguage}${location.pathname.substring(currentUrlLang.length + 1)}${location.search}`
        navigate(newPath, { replace: true })
      }
    }
  }, [isAuthenticated, queryLoading, patientLanguage, location, navigate])

  // Sync language changes to backend
  const syncLanguageToBackend = useCallback(
    async (language: SupportedLanguage) => {
      if (!isAuthenticated) return
      if (lastSyncedLanguage.current === language) return
      if (!languages.includes(language)) return

      try {
        await updateMyInfos({
          variables: {
            payload: { language },
          },
        })
        lastSyncedLanguage.current = language
      } catch (error) {
        console.error('Failed to sync language preference:', error)
      }
    },
    [isAuthenticated, updateMyInfos]
  )

  // Watch for language changes and sync to backend
  useEffect(() => {
    if (!isAuthenticated || queryLoading) return

    const currentLanguage = i18n.language as SupportedLanguage

    // Only sync if:
    // 1. We've done initial sync (to avoid overwriting on first load)
    // 2. Current language differs from what we last synced
    // 3. Current language differs from stored patient language
    if (
      hasInitialSynced.current &&
      currentLanguage !== lastSyncedLanguage.current &&
      currentLanguage !== patientLanguage &&
      languages.includes(currentLanguage)
    ) {
      syncLanguageToBackend(currentLanguage)
    }
  }, [i18n.language, isAuthenticated, queryLoading, patientLanguage, syncLanguageToBackend])

  // Mark as synced if patient has no language set (new user) and set initial language
  useEffect(() => {
    if (isAuthenticated && !queryLoading && !patientLanguage && !hasInitialSynced.current) {
      hasInitialSynced.current = true
      // Set initial language for new users
      const currentLanguage = i18n.language as SupportedLanguage
      if (languages.includes(currentLanguage)) {
        syncLanguageToBackend(currentLanguage)
      }
    }
  }, [isAuthenticated, queryLoading, patientLanguage, i18n.language, syncLanguageToBackend])

  return {
    syncLanguageToBackend,
    patientLanguage,
    isLoading: queryLoading,
  }
}
